import _ from "lodash";
import moment from "moment-timezone";
import * as z from "../../../framework";
import Appointment from "../../scheduler/appointment";

export function schedulerEventLoadOffset(args) {
    var start = args.startDate;
    var startDateOffset = new Date(start).getTimezoneOffset();

    var end = args.end;
    var endDateOffset = new Date(end).getTimezoneOffset();

    start.addMinutes(-startDateOffset);
    end.addMinutes(-endDateOffset);

    return args;
}

export function getDateDifference(args) {
    // This function is used by widgets by accessing z.scheduleUtil.getDateDifference

    var diffMs = (args.endDate - args.startDate);
    var diffDays;
    var diffHrs;
    var diffMins;

    if (args.noRound) {
        diffDays = diffMs / 24 / 60 / 60 / 1000; // days
        diffHrs = diffMs / 60 / 60 / 1000; // hours
        diffMins = diffMs / 60 / 1000; // minutes
    } else {
        diffDays = Math.round(diffMs / 24 / 60 / 60 / 1000); // days
        diffHrs = Math.round(diffMs / 60 / 60 / 1000); // hours
        diffMins = Math.round(diffMs / 60 / 1000); // minutes
    }

    return {
        ms: diffMs,
        days: diffDays,
        hrs: diffHrs,
        mins: diffMins
    };
}

//#region Appointment Start Time Helpers
var defaultAppointmentStartTime = new Date();
defaultAppointmentStartTime.setHours(0, 0, 0, 0);

function applyTime(scheduleDate: Date, timeToUse: Date): Date {
    scheduleDate = new Date(scheduleDate);
    timeToUse = new Date(timeToUse);

    scheduleDate.setHours(timeToUse.getHours());
    scheduleDate.setMinutes(timeToUse.getMinutes());
    scheduleDate.setSeconds(timeToUse.getSeconds());
    scheduleDate.setMilliseconds(timeToUse.getMilliseconds());

    return scheduleDate;
}

async function useTechStartTime(scheduleDate: Date, foundTech: any): Promise<any> {
    var techStartTime = (foundTech && foundTech.StartTime as Date);
    if (!techStartTime) {
        return useBusinessStartTime(scheduleDate);
    }

    scheduleDate = applyTime(scheduleDate, techStartTime);
    let techTimeZoneId = foundTech && foundTech.TimeZoneId;
    if (!techTimeZoneId) {
        return scheduleDate;
    }

    return await assignDateToTimeZone(scheduleDate, techTimeZoneId);
}

function useBusinessStartTime(scheduleDate: Date): Date {
    var businessTime = z.util.getDateOption("ScheduleBusinessHoursStart", defaultAppointmentStartTime);

    return applyTime(scheduleDate, businessTime);
}

//#endregion

export function setDefaultAppointmentStart(appointment: Appointment, scheduleDate: Date, serviceTech?: any) {
    var scheduleDateWithTime = scheduleDate;

    if (serviceTech == undefined) {
        if (appointment.AssignedTech) {
            serviceTech = appointment.AssignedTech;
        } else if (appointment.AssignedTechId) {
            z.ajax({
                url: `ServiceTech/${appointment.AssignedTechId}`
            }).done(function (response) {
                serviceTech = response.d.results;

                useTechStartTime(scheduleDate, serviceTech).then((startTime) => {
                    scheduleDateWithTime = startTime;
                });
            }).fail(function () {
                scheduleDateWithTime = useBusinessStartTime(scheduleDate);
            });
        } else {
            scheduleDateWithTime = useBusinessStartTime(scheduleDate);
        }
    } else {
        useTechStartTime(scheduleDate, serviceTech).then((startTime) => {
            scheduleDateWithTime = startTime;
        });
    }

    appointment.ScheduledDateTime = scheduleDateWithTime.toString();
}

export async function getDefaultAppointmentStart(scheduleDate: Date, serviceTech?: any): Promise<any> {
    let useTechTime = z.util.getBooleanOption("SetTimeBasedOffTech", false);

    if (useTechTime && serviceTech) {
        return await useTechStartTime(scheduleDate, serviceTech);
    }

    return useBusinessStartTime(scheduleDate);
}

async function getTimeZones(): Promise<any> {
    let timeZones = z.util.getLocalStorage("TimeZones") || [];

    if (timeZones.length > 0) {
        return timeZones;
    } else {
        let select = [
                "ZoneId",
                "TimeZoneZones/*"
            ].join(","),
            correlationGroupId = makeGuid();

        let data = await z.ajax({
            url: `TimeZoneSettings?$select=${select}&$expand=TimeZoneZones`,
            correlationGroupId: correlationGroupId
        });

        timeZones = data.d.results;

        let d = new Date(),
            seconds = d.getTime() / 1000,
            timeZoneRequests = [];

        for (var index = 0; index < timeZones.length; index++) {
            let timeZone = timeZones[index],
                request = z.ajax({
                    url: `TimeZone?$filter=Id eq ${timeZone.ZoneId} AND TimeStart lt ${Math.floor(seconds)}&$top=1&$orderby=TimeStart desc`,
                    correlationGroupId: correlationGroupId
                }).done((timeZoneData) => {
                    timeZone.TimeZone = timeZoneData.d.results[0];
                });

            timeZoneRequests.push(request);
        }

        await Promise.all(timeZoneRequests);

        z.util.setLocalStorage("TimeZones", timeZones);
        return timeZones;
    }
}

async function assignDateToTimeZone(datetime: Date, timeZoneId: number): Promise<any> {
    let timeZones = await getTimeZones();
    if (!timeZones || timeZones.length == 0) {
        return datetime;
    } else {
        let timeZone = _.find(timeZones, tz => tz.ZoneId == timeZoneId);
        if (timeZone) {
            let zoneName = timeZone.TimeZoneZones && timeZone.TimeZoneZones.ZoneName;
            if (zoneName) {
                let targetDate = moment.tz(datetime, zoneName),
                    targetOffset = (targetDate as any)._offset,
                    sourceOffset = new Date().getTimezoneOffset(),
                    offsetDifference = Math.abs(targetOffset) - sourceOffset,
                    zoneAjustedDate = targetDate.toDate().addMinutes(offsetDifference);

                return applyTime(datetime, zoneAjustedDate);
            } else {
                return datetime;
            }
        } else {
            return datetime;
        }
    }
}

function makeGuid() {
    let sGuid = "";

    for (let index = 0; index < 32; index++) {
        sGuid += Math.floor(Math.random() * 0xF).toString(0xF);
    }

    return sGuid;
}
